/**
 * Browser log-shipper. Chains onto `console.error`/`console.warn` and the
 * global `error`/`unhandledrejection` events, buffers structured lines and
 * POSTs them to the backend's `/client/log` endpoint. See decisions-log.md.
 *
 * Design notes:
 *  - Original console methods are always called first, then we capture.
 *  - A re-entrancy guard prevents the patched console (invoked transitively
 *    during a flush, e.g. by fetch polyfills) from re-capturing.
 *  - On any flush failure (4xx/5xx/network) the batch is dropped silently;
 *    we never console.error from inside the shipper to avoid an infinite loop.
 *  - Per-minute rate cap + hard buffer cap keep memory bounded.
 *  - Idempotent and SSR-safe.
 *
 * Diagnostics extensions (level-gated + redacted):
 *  - Every shipped line carries a per-page-load `session_id` (correlation id).
 *  - Secrets are redacted (msg/stack/url/extra) BEFORE buffering.
 *  - The shipper polls `GET /client/loglevel` to learn the effective verbosity
 *    and only buffers lines the level permits (errors/warns always pass). The
 *    poll doubles as the activity ping that decrements the online-budget.
 *  - When the effective level >= "debug" the rate cap is raised and rich
 *    WebRTC/LiveKit + device/environment diagnostics collectors become active
 *    (see {@link attachWebrtcDiagnostics}, {@link logDiagEnvironment},
 *    {@link logDiagBreadcrumb}). All collectors are duck-typed and best-effort;
 *    shared imports no `livekit-client` (browser/SSR-safe + dependency-free).
 */
import type {
  ClientLogLine,
  ClientLogRequest,
  ClientLogLevelResponse,
  LogLevel,
} from "./admin.js";
import { redactString, redactUrl, redactObject } from "./redact.js";

export interface LogShipperOptions {
  backendUrl: string;
  source: "admin" | "student" | "device";
  /** Max captured entries per rolling 60s window. Default 60. */
  maxPerMinute?: number;
}

export interface LogShipperHandle {
  /** Force an immediate flush of the buffer. Resolves after the POST settles. */
  flushNow: () => Promise<void>;
  /** Current number of buffered (not-yet-flushed) entries. For tests. */
  _bufferLength: () => number;
  /** The current effective log level (after the last successful poll). For tests. */
  _currentLevel: () => LogLevel;
  /** Force an immediate loglevel poll. Resolves after the GET settles. For tests. */
  _pollNow: () => Promise<void>;
}

const FLUSH_INTERVAL_MS = 5_000;
const FLUSH_AT_COUNT = 20;
const HARD_CAP = 200;
const RATE_WINDOW_MS = 60_000;

/** Default re-poll interval when the backend doesn't supply `poll_after_ms`. */
const DEFAULT_POLL_AFTER_MS = 30_000;
/** Rate cap (per minute) raised automatically while level >= debug. */
const DEBUG_MAX_PER_MINUTE = 600;
/** WebRTC stats sampling cadence while level >= debug. */
const WEBRTC_SAMPLE_MS = 10_000;
/** Ring-buffer capacity for breadcrumbs (flushed with the next error). */
const BREADCRUMB_CAP = 20;
/**
 * Hard cap on ALWAYS-ON failure lines (see {@link logDiagFailure}) per rolling
 * minute. Connect failures ship even when debug is off — that is the whole
 * point — but a flapping network must not turn every student into a log
 * firehose, so failures get their own tight budget on top of the normal cap.
 */
const FAILURE_MAX_PER_MINUTE = 6;

/** Numeric order map (least → most verbose). Higher = more verbose. */
const LEVEL_ORDER: Record<LogLevel, number> = {
  off: 0,
  error: 1,
  warn: 2,
  info: 3,
  debug: 4,
  trace: 5,
};

function order(level: LogLevel): number {
  return LEVEL_ORDER[level] ?? LEVEL_ORDER.info;
}

let installed = false;

/**
 * Module-level handle to the live shipper instance, so the free-standing
 * diagnostics helpers ({@link logDiagEnvironment}, {@link logDiagBreadcrumb},
 * {@link attachWebrtcDiagnostics}) can emit through it without a reference. Set
 * by {@link installLogShipper}; null when not installed / SSR / inert.
 */
let active: ActiveShipper | null = null;

/** A stable per-page-load correlation id, generated once on first install. */
let pageSessionId: string | undefined;

/** Generate a UUID-ish id with a crypto fallback (older browsers / SSR). */
function makeSessionId(): string {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c && typeof c.randomUUID === "function") return c.randomUUID();
  } catch {
    /* ignore */
  }
  // Fallback: timestamp + random. Not RFC-4122 but unique enough for a load.
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Internal interface the diagnostics helpers drive. */
interface ActiveShipper {
  /** Emit a structured line at `level`, gated by the current effective level. */
  log: (level: LogLevel, msg: string, extra?: Record<string, unknown>) => void;
  /** True when the effective level is >= debug (rich collectors enabled). */
  isDebug: () => boolean;
  /** Push a breadcrumb into the ring buffer (flushed with the next error). */
  breadcrumb: (msg: string, extra?: Record<string, unknown>) => void;
  /**
   * Emit a FAILURE line at warn/error. Unlike {@link ActiveShipper.log} this is
   * NOT gated on debug — a connect failure is exactly the event we cannot ask
   * the user to reproduce with debug enabled. Still gated by the effective
   * level ("off" silences everything) and by its own tight rate budget.
   */
  failure: (
    level: "warn" | "error",
    msg: string,
    extra?: Record<string, unknown>,
  ) => void;
  source: LogShipperOptions["source"];
}

/** Safely stringify a single console/error argument. */
function stringifyArg(arg: unknown): string {
  if (arg instanceof Error) {
    return arg.stack ? `${arg.message}\n${arg.stack}` : arg.message;
  }
  if (typeof arg === "string") return arg;
  if (
    arg === null ||
    arg === undefined ||
    typeof arg === "number" ||
    typeof arg === "boolean" ||
    typeof arg === "bigint" ||
    typeof arg === "symbol"
  ) {
    return String(arg);
  }
  try {
    return JSON.stringify(arg);
  } catch {
    try {
      return String(arg);
    } catch {
      return "[unserializable]";
    }
  }
}

function stringifyArgs(args: unknown[]): string {
  return args.map(stringifyArg).join(" ");
}

/** Extract a stack from the first Error among the args, if any. */
function firstStack(args: unknown[]): string | undefined {
  for (const a of args) {
    if (a instanceof Error && a.stack) return a.stack;
  }
  return undefined;
}

/**
 * Install the browser log-shipper. Idempotent and SSR-safe. Returns a handle
 * exposing `flushNow`/`_bufferLength`/`_currentLevel`/`_pollNow` for tests; on a
 * no-op (already installed or no window) the handle's methods are inert.
 *
 * Beyond chaining the console + global error events this now (a) attaches a
 * per-page-load `session_id` to every line, (b) redacts secrets before buffering,
 * (c) polls `GET {backendUrl}/client/loglevel` to learn the effective level and
 * only buffers what the level permits, and (d) enables rich diagnostics
 * collectors when the level is debug or higher.
 */
export function installLogShipper(opts: LogShipperOptions): LogShipperHandle {
  const inert: LogShipperHandle = {
    flushNow: () => Promise.resolve(),
    _bufferLength: () => 0,
    _currentLevel: () => "info",
    _pollNow: () => Promise.resolve(),
  };

  if (typeof window === "undefined") return inert;
  if (installed) return inert;
  installed = true;

  if (!pageSessionId) pageSessionId = makeSessionId();
  const sessionId = pageSessionId;

  const baseMaxPerMinute = opts.maxPerMinute ?? 60;
  const buffer: ClientLogLine[] = [];

  // Effective level + the admin debug session driving it (null ⇒ default).
  let currentLevel: LogLevel = "info";
  let activeSessionId: number | null = null;

  // Breadcrumb ring buffer: recent context emitted alongside the next error.
  const breadcrumbs: { ts: number; msg: string; extra?: Record<string, unknown> }[] =
    [];

  // Rolling-window rate cap: timestamps of captured entries in the last 60s.
  let windowStart = Date.now();
  let windowCount = 0;

  // Separate rolling budget for always-on failure lines (see `failure`).
  let failureWindowStart = Date.now();
  let failureWindowCount = 0;

  // Re-entrancy guard: while flushing we must not capture our own logging.
  let flushing = false;

  let timer: ReturnType<typeof setInterval> | undefined;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;

  /** The effective per-minute cap: raised while level >= debug. */
  function effectiveMaxPerMinute(): number {
    return order(currentLevel) >= order("debug")
      ? Math.max(baseMaxPerMinute, DEBUG_MAX_PER_MINUTE)
      : baseMaxPerMinute;
  }

  function allowedByRate(): boolean {
    const now = Date.now();
    if (now - windowStart >= RATE_WINDOW_MS) {
      windowStart = now;
      windowCount = 0;
    }
    if (windowCount >= effectiveMaxPerMinute()) return false;
    windowCount++;
    return true;
  }

  /** True when a line at `level` should be buffered given the current level. */
  function levelPermits(level: LogLevel): boolean {
    if (currentLevel === "off") return false;
    return order(level) <= order(currentLevel);
  }

  /**
   * Core capture path. Redacts msg/stack/url/extra, attaches the session id and
   * pushes onto the buffer. `level` is gated against the effective level by the
   * callers (console/global → capture; structured → log). Never throws.
   */
  function push(
    level: LogLevel,
    msg: string,
    stack?: string,
    extra?: Record<string, unknown>,
  ): void {
    if (flushing) return;
    if (!allowedByRate()) return;

    const line: ClientLogLine = {
      ts: Date.now(),
      level,
      source: opts.source,
      msg: redactString(msg),
      session_id: sessionId,
    };
    if (stack) line.stack = redactString(stack);
    try {
      line.url = redactUrl(window.location.href);
    } catch {
      /* ignore */
    }
    if (extra && typeof extra === "object") {
      try {
        line.extra = redactObject(extra);
      } catch {
        /* ignore */
      }
    }

    buffer.push(line);
    // Hard cap: drop oldest to keep memory bounded.
    while (buffer.length > HARD_CAP) buffer.shift();

    if (buffer.length >= FLUSH_AT_COUNT) {
      void flush();
    }
  }

  /**
   * Capture an error/warn line. These are ALWAYS shipped (error/warn ≤ info, so
   * the level check passes for any non-"off" level). On an error we also flush
   * the accumulated breadcrumbs as a single context line.
   */
  function capture(level: "error" | "warn", msg: string, stack?: string): void {
    if (!levelPermits(level)) return;
    if (level === "error" && breadcrumbs.length > 0) {
      flushBreadcrumbs();
    }
    push(level, msg, stack);
  }

  /** Structured/diagnostic emit, gated strictly by the effective level. */
  function log(
    level: LogLevel,
    msg: string,
    extra?: Record<string, unknown>,
  ): void {
    if (!levelPermits(level)) return;
    push(level, msg, undefined, extra);
  }

  /**
   * Always-on failure emit. Ships warn/error diagnostics even when the
   * effective level is the default (info) and debug was never turned on, so a
   * one-off connect failure in the field is debuggable without asking the
   * family to reproduce it. Bounded by {@link FAILURE_MAX_PER_MINUTE} so a
   * flapping connection cannot spam the backend; over budget we silently drop.
   * Breadcrumbs ride along so the lead-up is visible. Never throws.
   */
  function failure(
    level: "warn" | "error",
    msg: string,
    extra?: Record<string, unknown>,
  ): void {
    if (currentLevel === "off") return;
    const now = Date.now();
    if (now - failureWindowStart >= RATE_WINDOW_MS) {
      failureWindowStart = now;
      failureWindowCount = 0;
    }
    if (failureWindowCount >= FAILURE_MAX_PER_MINUTE) return;
    failureWindowCount++;
    if (breadcrumbs.length > 0) flushBreadcrumbs();
    push(level, msg, undefined, extra);
  }

  /** Record a breadcrumb. Emitted directly when debug; always kept in the ring. */
  function breadcrumb(msg: string, extra?: Record<string, unknown>): void {
    breadcrumbs.push({ ts: Date.now(), msg, extra });
    while (breadcrumbs.length > BREADCRUMB_CAP) breadcrumbs.shift();
    // When verbose, also emit it live so the timeline is visible without an error.
    if (order(currentLevel) >= order("debug")) {
      log("debug", `breadcrumb: ${msg}`, extra);
    }
  }

  /** Flush the breadcrumb ring as one compact context line, then clear it. */
  function flushBreadcrumbs(): void {
    if (breadcrumbs.length === 0) return;
    const crumbs = breadcrumbs.splice(0, breadcrumbs.length);
    // Breadcrumbs ride out with errors regardless of level (they precede an
    // error we are already shipping), so push directly at "info".
    push("info", "breadcrumbs", undefined, {
      breadcrumbs: crumbs.map((c) => ({ ts: c.ts, msg: c.msg, ...c.extra })),
    });
  }

  async function flush(): Promise<void> {
    if (flushing) return;
    if (buffer.length === 0) return;

    const lines = buffer.splice(0, buffer.length);
    const body: ClientLogRequest = { lines };

    flushing = true;
    try {
      await fetch(`${opts.backendUrl}/client/log`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      // On 4xx/5xx we deliberately do nothing: drop the batch silently.
    } catch {
      // Network error: drop silently. Never console.error here (would loop).
    } finally {
      flushing = false;
    }
  }

  /**
   * Poll the backend for the effective level. Updates `currentLevel` +
   * `activeSessionId` and (re)arms the next poll using the server's
   * `poll_after_ms` hint. Doubles as the activity ping (the backend decrements
   * the online-budget on each poll). Best-effort: never throws and, on failure,
   * keeps the previous level and re-arms with the default cadence.
   */
  async function poll(): Promise<void> {
    let nextAfter = DEFAULT_POLL_AFTER_MS;
    try {
      const res = await fetch(`${opts.backendUrl}/client/loglevel`, {
        method: "GET",
        credentials: "include",
      });
      if (res && (res as Response).ok) {
        const data = (await (res as Response).json()) as ClientLogLevelResponse;
        if (data && typeof data.level === "string") {
          currentLevel = data.level;
        }
        if (data && (typeof data.session_id === "number" || data.session_id === null)) {
          activeSessionId = data.session_id;
        }
        if (
          data &&
          typeof data.poll_after_ms === "number" &&
          data.poll_after_ms > 0
        ) {
          nextAfter = data.poll_after_ms;
        }
      }
    } catch {
      // Keep the previous level; re-arm with the default cadence.
    } finally {
      // Re-arm only while the page is open (timer cleared on pagehide is fine —
      // the browser tears the page down anyway).
      pollTimer = setTimeout(() => {
        void poll();
      }, nextAfter);
      if (
        pollTimer &&
        typeof (pollTimer as { unref?: () => void }).unref === "function"
      ) {
        (pollTimer as unknown as { unref: () => void }).unref();
      }
    }
  }

  // --- Patch console (chain to original) ---
  const origError = console.error.bind(console);
  const origWarn = console.warn.bind(console);

  console.error = (...args: unknown[]): void => {
    origError(...args);
    capture("error", stringifyArgs(args), firstStack(args));
  };
  console.warn = (...args: unknown[]): void => {
    origWarn(...args);
    capture("warn", stringifyArgs(args), firstStack(args));
  };

  // --- Global error events ---
  window.addEventListener("error", (ev: ErrorEvent) => {
    const err = ev.error;
    let msg: string;
    let stack: string | undefined;
    if (err instanceof Error) {
      msg = err.message;
      stack = err.stack;
    } else {
      msg = ev.message || "error";
    }
    capture("error", msg, stack);
  });

  window.addEventListener("unhandledrejection", (ev: PromiseRejectionEvent) => {
    const reason = ev.reason;
    let msg: string;
    let stack: string | undefined;
    if (reason instanceof Error) {
      msg = `Unhandled rejection: ${reason.message}`;
      stack = reason.stack;
    } else {
      msg = `Unhandled rejection: ${stringifyArg(reason)}`;
    }
    capture("error", msg, stack);
  });

  // --- Periodic flush ---
  timer = setInterval(() => {
    void flush();
  }, FLUSH_INTERVAL_MS);
  // Don't keep a Node/test event loop alive on account of the shipper.
  if (timer && typeof (timer as { unref?: () => void }).unref === "function") {
    (timer as unknown as { unref: () => void }).unref();
  }

  // Best-effort final flush on page hide/unload.
  window.addEventListener("pagehide", () => {
    void flush();
  });

  // Publish the active-shipper hook so the free-standing diagnostics helpers
  // can route through this instance.
  active = {
    log,
    isDebug: () => order(currentLevel) >= order("debug"),
    breadcrumb,
    failure,
    source: opts.source,
  };

  // Kick off the level poll (also the activity ping). Best-effort; never throws.
  void poll();

  return {
    flushNow: flush,
    _bufferLength: () => buffer.length,
    _currentLevel: () => currentLevel,
    _pollNow: poll,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Rich diagnostics collectors (only emit when effective level >= "debug").
// All duck-typed + best-effort; shared never hard-imports livekit-client.
// ─────────────────────────────────────────────────────────────────────────────

export interface WebrtcDiagnosticsOptions {
  /** Stats sampling cadence in ms (default 10000). */
  sampleMs?: number;
  /** Tag included on every emitted line (e.g. "subscriber"/"publisher"). */
  label?: string;
}

/** Read a property off an unknown value without throwing. */
function get(obj: unknown, key: string): unknown {
  if (obj === null || typeof obj !== "object") return undefined;
  try {
    return (obj as Record<string, unknown>)[key];
  } catch {
    return undefined;
  }
}

/** Best-effort resolve an RTCPeerConnection-like object from a room/pc/engine. */
function resolvePeerConnection(room: unknown): unknown {
  // Direct RTCPeerConnection-like (has getStats).
  if (typeof get(room, "getStats") === "function") return room;
  // LiveKit Room → engine → pcManager → subscriber/publisher.pc (varies by ver).
  const engine = get(room, "engine");
  const candidates: unknown[] = [];
  const pcManager = get(engine, "pcManager");
  candidates.push(
    get(get(pcManager, "subscriber"), "pc"),
    get(get(pcManager, "publisher"), "pc"),
    get(get(engine, "subscriber"), "pc"),
    get(get(engine, "publisher"), "pc"),
  );
  for (const c of candidates) {
    if (typeof get(c, "getStats") === "function") return c;
  }
  return null;
}

/**
 * Compactly summarise an RTCStatsReport into a bounded, redaction-friendly bag.
 * Collects rtt, jitter, packetsLost, byte deltas, fps and resolution where the
 * report exposes them. `prev` carries the last sample's cumulative byte counts
 * for delta computation; the function returns the updated counters too.
 */
function summariseStats(
  report: unknown,
  prev: { bytesSent: number; bytesReceived: number },
): {
  summary: Record<string, unknown>;
  next: { bytesSent: number; bytesReceived: number };
} {
  const summary: Record<string, unknown> = {};
  let bytesSent = 0;
  let bytesReceived = 0;
  let rtt: number | undefined;
  let inbound: Record<string, unknown> | undefined;
  let outbound: Record<string, unknown> | undefined;

  const each = get(report, "forEach");
  if (typeof each === "function") {
    try {
      (report as { forEach: (cb: (s: unknown) => void) => void }).forEach(
        (s: unknown) => {
          const type = get(s, "type");
          if (type === "candidate-pair") {
            const nominated = get(s, "nominated");
            const stateOk = get(s, "state") === "succeeded";
            if (nominated === true || stateOk) {
              const crtt = get(s, "currentRoundTripTime");
              if (typeof crtt === "number") rtt = crtt;
            }
          } else if (type === "inbound-rtp") {
            const br = get(s, "bytesReceived");
            if (typeof br === "number") bytesReceived += br;
            inbound = {
              jitter: get(s, "jitter"),
              packetsLost: get(s, "packetsLost"),
              fps: get(s, "framesPerSecond"),
              w: get(s, "frameWidth"),
              h: get(s, "frameHeight"),
              kind: get(s, "kind"),
            };
          } else if (type === "outbound-rtp") {
            const bs = get(s, "bytesSent");
            if (typeof bs === "number") bytesSent += bs;
            outbound = {
              fps: get(s, "framesPerSecond"),
              w: get(s, "frameWidth"),
              h: get(s, "frameHeight"),
              kind: get(s, "kind"),
            };
          }
        },
      );
    } catch {
      /* ignore — best-effort */
    }
  }

  if (typeof rtt === "number") summary.rtt_ms = Math.round(rtt * 1000);
  if (inbound) {
    if (typeof inbound.jitter === "number") summary.jitter_ms = Math.round((inbound.jitter as number) * 1000);
    if (typeof inbound.packetsLost === "number") summary.packetsLost = inbound.packetsLost;
    if (typeof inbound.fps === "number") summary.in_fps = inbound.fps;
    if (typeof inbound.w === "number" && typeof inbound.h === "number") {
      summary.in_res = `${inbound.w}x${inbound.h}`;
    }
  }
  if (outbound) {
    if (typeof outbound.fps === "number") summary.out_fps = outbound.fps;
    if (typeof outbound.w === "number" && typeof outbound.h === "number") {
      summary.out_res = `${outbound.w}x${outbound.h}`;
    }
  }
  summary.bytesRecvDelta = Math.max(0, bytesReceived - prev.bytesReceived);
  summary.bytesSentDelta = Math.max(0, bytesSent - prev.bytesSent);

  return { summary, next: { bytesSent, bytesReceived } };
}

/**
 * Instrument a LiveKit Room (or a raw RTCPeerConnection) for rich WebRTC
 * diagnostics. Best-effort + duck-typed: pass `any`/unknown; the function
 * feature-detects everything and never throws. Only active when the effective
 * log level is >= "debug" (otherwise a safe no-op).
 *
 * When active it:
 *  - subscribes to the room's connection-state / reconnecting / disconnected
 *    events (if it exposes a LiveKit-style `.on()`), logging transitions at
 *    "debug";
 *  - every ~10s (configurable) samples `getStats()` off the room's engine pc (or
 *    the passed RTCPeerConnection) and logs a compact, redacted summary of rtt,
 *    jitter, packetsLost, byte deltas, fps and resolution at "debug".
 *
 * Returns a cleanup function that removes listeners + stops the sampler. The
 * cleanup is always safe to call (idempotent, never throws). No-ops safely when
 * `room` is null or the level is below debug.
 */
export function attachWebrtcDiagnostics(
  room: unknown,
  opts: WebrtcDiagnosticsOptions = {},
): () => void {
  const noop = (): void => {};
  if (!active) return noop;
  if (room === null || room === undefined) return noop;
  if (!active.isDebug()) return noop;

  const label = opts.label ?? "webrtc";
  const sampleMs = opts.sampleMs ?? WEBRTC_SAMPLE_MS;
  const shipper = active;

  // --- Connection-state event listeners (LiveKit-style `.on`/`.off`). ---
  const on = get(room, "on");
  const off = get(room, "off");
  const registered: { event: string; cb: (...a: unknown[]) => void }[] = [];
  const bind = (event: string, cb: (...a: unknown[]) => void): void => {
    if (typeof on === "function") {
      try {
        (on as (e: string, cb: unknown) => void).call(room, event, cb);
        registered.push({ event, cb });
      } catch {
        /* ignore */
      }
    }
  };

  // LiveKit RoomEvent string values are stable across versions.
  bind("connectionStateChanged", (state: unknown) => {
    shipper.log("debug", `${label} connectionState`, { state: String(state) });
  });
  bind("reconnecting", () => {
    shipper.log("debug", `${label} reconnecting`);
  });
  bind("reconnected", () => {
    shipper.log("debug", `${label} reconnected`);
  });
  bind("disconnected", (reason: unknown) => {
    shipper.log("debug", `${label} disconnected`, { reason: String(reason) });
  });

  // --- Periodic stats sampler. ---
  let prev = { bytesSent: 0, bytesReceived: 0 };
  let stopped = false;
  const sampler = setInterval(() => {
    // Stop sampling the moment the level drops below debug.
    if (stopped || !shipper.isDebug()) return;
    const pc = resolvePeerConnection(room);
    const getStats = get(pc, "getStats");
    if (typeof getStats !== "function") return;
    try {
      const p = (getStats as () => Promise<unknown>).call(pc);
      if (p && typeof (p as Promise<unknown>).then === "function") {
        (p as Promise<unknown>)
          .then((report) => {
            if (stopped || !shipper.isDebug()) return;
            const { summary, next } = summariseStats(report, prev);
            prev = next;
            shipper.log("debug", `${label} stats`, summary);
          })
          .catch(() => {
            /* ignore — best-effort */
          });
      }
    } catch {
      /* ignore — best-effort */
    }
  }, sampleMs);
  if (
    sampler &&
    typeof (sampler as { unref?: () => void }).unref === "function"
  ) {
    (sampler as unknown as { unref: () => void }).unref();
  }

  return (): void => {
    if (stopped) return;
    stopped = true;
    try {
      clearInterval(sampler);
    } catch {
      /* ignore */
    }
    if (typeof off === "function") {
      for (const { event, cb } of registered) {
        try {
          (off as (e: string, cb: unknown) => void).call(room, event, cb);
        } catch {
          /* ignore */
        }
      }
    }
  };
}

/**
 * Capture a one-shot snapshot of the device/environment (user-agent, network
 * info, viewport, online state, PWA/standalone, page visibility) at "debug".
 * No-op when not installed or below debug. Best-effort; never throws.
 */
export function logDiagEnvironment(): void {
  if (!active || !active.isDebug()) return;
  if (typeof window === "undefined") return;
  const extra: Record<string, unknown> = {};
  try {
    const nav = (globalThis as { navigator?: Navigator }).navigator;
    if (nav) {
      extra.userAgent = nav.userAgent;
      extra.online = nav.onLine;
      const conn = get(nav, "connection");
      if (conn) {
        extra.netEffectiveType = get(conn, "effectiveType");
        extra.netDownlink = get(conn, "downlink");
        extra.netRtt = get(conn, "rtt");
      }
    }
    extra.viewport = {
      w: window.innerWidth,
      h: window.innerHeight,
      dpr: window.devicePixelRatio,
    };
    const doc = (globalThis as { document?: Document }).document;
    if (doc) {
      extra.visibility = doc.visibilityState;
    }
    // PWA / standalone detection (iOS uses navigator.standalone).
    let standalone = false;
    try {
      const mm = (globalThis as { matchMedia?: (q: string) => { matches: boolean } })
        .matchMedia;
      if (typeof mm === "function") {
        standalone = mm("(display-mode: standalone)").matches === true;
      }
    } catch {
      /* ignore */
    }
    const navStandalone = get(globalThis as unknown, "navigator");
    if (get(navStandalone, "standalone") === true) standalone = true;
    extra.standalone = standalone;
  } catch {
    /* ignore — best-effort */
  }
  active.log("debug", "diag environment", extra);
}

/**
 * Record a lifecycle breadcrumb. Breadcrumbs are kept in a small ring buffer
 * and flushed alongside the next captured error (so the lead-up to a failure is
 * visible). When the effective level is >= "debug" each breadcrumb is also
 * emitted live. No-op when not installed. Best-effort; never throws.
 */
export function logDiagBreadcrumb(
  msg: string,
  extra?: Record<string, unknown>,
): void {
  if (!active) return;
  try {
    active.breadcrumb(msg, extra);
  } catch {
    /* ignore */
  }
}

/**
 * Ship a CONNECT/MEDIA FAILURE line regardless of whether debug logging is on.
 *
 * Rationale: `logDiagBreadcrumb` + {@link attachWebrtcDiagnostics} only emit at
 * level >= debug, which means the failures we most need (a student who cannot
 * join at all) are exactly the ones we never see. Failures are rare and
 * rate-limited ({@link FAILURE_MAX_PER_MINUTE}/min) so the always-on path stays
 * cheap. The usual redaction still applies to `msg` and `extra`.
 *
 * CALLER CONTRACT: `extra` must never contain credentials. TURN
 * username/credential are secrets — log the URL scheme+host only.
 *
 * No-op when not installed or the level is "off". Best-effort; never throws.
 */
export function logDiagFailure(
  msg: string,
  extra?: Record<string, unknown>,
  level: "warn" | "error" = "warn",
): void {
  if (!active) return;
  try {
    active.failure(level, msg, extra);
  } catch {
    /* ignore */
  }
}

/** Test-only: reset module-level install state. Not exported in index. */
export function __resetLogShipperForTests(): void {
  installed = false;
  active = null;
  pageSessionId = undefined;
}
